import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PencilIcon, TrashIcon, EyeIcon, PlusIcon, DocumentDuplicateIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import api from '../../services/api';

import { useUserPermissions } from '../../hooks/useUserPermissions';
import { useDateFormatter } from '../../hooks/useDateFormatter';
import TableColumnFilter from '../../components/TableColumnFilter';
import ActiveFilterChips, { FilterChip } from '../../components/ActiveFilterChips';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const typeOptions = [
  { value: 'Service_Fee', label: 'Service Fee' },
  { value: 'Service_Flat', label: 'Service Flat' },
];

const statusOptions = [
  { value: 'false', label: 'Active' },
  { value: 'true', label: 'Inactive' },
];

interface Offer {
  id: string;
  name: string;
  offerType: string;
  slaCategory: string;
}

interface ServicePlan {
  id: string;
  name: string;
  type: string;
  startDate: string;
  endDate: string;
  price: number;
  priceEffectiveDate: string;
  inactive: boolean;
  isTaxable: boolean;
  planProvisioningDate: string | null;
  offer: Offer;
  createdAt: string;
  updatedAt: string;
}

const PlanList: React.FC = () => {
  const { formatDate } = useDateFormatter();
  const [searchParams, setSearchParams] = useSearchParams();
  const patchSearchParams = useCallback(
    (updates: Record<string, string | null | undefined>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, val] of Object.entries(updates)) {
            if (val === null || val === undefined || val === '') {
              next.delete(key);
            } else {
              next.set(key, val);
            }
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '');
  const appliedSearch = searchParams.get('search') ?? '';
  const filterName = searchParams.get('name') ?? '';
  const filterOffer = searchParams.get('offer') ?? '';
  const filterType = searchParams.get('type') ?? '';
  const filterInactive = searchParams.get('inactive') ?? '';
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [offset, setOffset] = useState(0);
  const navigate = useNavigate();
  const { canViewEntity, canCreateEntity, canUpdateEntity, loading: permissionsLoading } = useUserPermissions();

  const canAccessServicePlans = canViewEntity('service_plans');
  const canCreateServicePlans = canCreateEntity('service_plans');
  const canUpdateServicePlans = canUpdateEntity('service_plans');
  const canDeleteServicePlans = canUpdateEntity('service_plans');

  useEffect(() => {
    setSearchInput(appliedSearch);
  }, [appliedSearch]);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: pageSize.toString(),
        offset: offset.toString(),
      });
      if (appliedSearch) params.append('search', appliedSearch);
      if (filterName) params.append('name', filterName);
      if (filterOffer) params.append('offerName', filterOffer);
      if (filterType) params.append('type', filterType);
      if (filterInactive) params.append('inactive', filterInactive);

      const response = await api.get(`/service-plans?${params}`);
      setPlans(response.data.plans);
      setTotal(response.data.total);
      setError(null);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 'Failed to fetch service plans';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pageSize, offset, appliedSearch, filterName, filterOffer, filterType, filterInactive]);

  useEffect(() => {
    if (permissionsLoading) return;
    if (!canAccessServicePlans) {
      navigate('/dashboard');
      return;
    }
    fetchPlans();
  }, [canAccessServicePlans, navigate, fetchPlans, permissionsLoading]);

  const handleDelete = async (id: string, planName: string) => {
    confirmAlert({
      title: 'Confirm Delete',
      message: `Are you sure you want to delete plan for "${planName}"? This action cannot be undone.`,
      buttons: [
        {
          label: 'Cancel',
          onClick: () => {},
          className: 'px-6 py-2.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm'
        },
        {
          label: 'Delete',
          onClick: async () => {
            try {
              await api.delete(`/service-plans/${id}`);
              toast.success('Service plan deleted successfully');
              fetchPlans();
            } catch (err: any) {
              const errorMessage = err.response?.data?.error || 'Failed to delete service plan';
              toast.error(errorMessage);
            }
          },
          className: 'px-6 py-2.5 text-sm text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 shadow-sm'
        }
      ]
    });
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    patchSearchParams({ search: searchInput.trim() || null });
    setOffset(0);
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchInput(value);
    if (value === '') {
      patchSearchParams({ search: null });
      setOffset(0);
    }
  };

  const renderColumnHeader = useCallback(
    (columnKey: string, columnLabel: string) => {
      switch (columnKey) {
        case 'planName':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterName}
              placeholder="Filter by plan name..."
              onChange={(v) => {
                patchSearchParams({ name: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'offer':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="text"
              value={filterOffer}
              placeholder="Filter by offer..."
              onChange={(v) => {
                patchSearchParams({ offer: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'type':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={typeOptions}
              value={filterType}
              placeholder="All Types"
              onChange={(v) => {
                patchSearchParams({ type: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        case 'status':
          return (
            <TableColumnFilter
              label={columnLabel}
              type="single-select"
              options={statusOptions}
              value={filterInactive}
              placeholder="All Statuses"
              onChange={(v) => {
                patchSearchParams({ inactive: typeof v === 'string' && v ? v : null });
                setOffset(0);
              }}
            />
          );
        default:
          return columnLabel;
      }
    },
    [filterName, filterOffer, filterType, filterInactive, patchSearchParams]
  );

  const activeFilterChips: FilterChip[] = useMemo(() => {
    const chips: FilterChip[] = [];
    if (appliedSearch) {
      chips.push({ id: 'search', label: 'Search', value: `"${appliedSearch}"`, color: 'blue' });
    }
    if (filterName) {
      chips.push({ id: 'name', label: 'Plan Name', value: filterName, color: 'blue' });
    }
    if (filterOffer) {
      chips.push({ id: 'offer', label: 'Offer', value: filterOffer, color: 'blue' });
    }
    if (filterType) {
      const typeLabel = typeOptions.find((o) => o.value === filterType)?.label || filterType;
      chips.push({ id: 'type', label: 'Type', value: typeLabel, color: 'teal' });
    }
    if (filterInactive) {
      const statusLabel = statusOptions.find((o) => o.value === filterInactive)?.label || filterInactive;
      chips.push({ id: 'inactive', label: 'Status', value: statusLabel, color: 'purple' });
    }
    return chips;
  }, [appliedSearch, filterName, filterOffer, filterType, filterInactive]);

  const handleRemoveFilterChip = (chipId: string) => {
    switch (chipId) {
      case 'search':
        setSearchInput('');
        patchSearchParams({ search: null });
        break;
      case 'name':
        patchSearchParams({ name: null });
        break;
      case 'offer':
        patchSearchParams({ offer: null });
        break;
      case 'type':
        patchSearchParams({ type: null });
        break;
      case 'inactive':
        patchSearchParams({ inactive: null });
        break;
    }
    setOffset(0);
  };

  const handleClearAllFilters = () => {
    setSearchInput('');
    patchSearchParams({
      search: null,
      name: null,
      offer: null,
      type: null,
      inactive: null,
    });
    setOffset(0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getTypeBadgeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'service_fee':
        return 'bg-purple-100 text-purple-800';
      case 'service_flat':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const currentPage = Math.floor(offset / pageSize) + 1;
  const totalPages = Math.ceil(total / pageSize);

  const goToPage = (page: number) => {
    setOffset((page - 1) * pageSize);
  };

  const getPageNumbers = () => {
    const pages: (number | string)[] = [];
    const showPages = 5;

    if (totalPages <= showPages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else if (currentPage <= 3) {
      for (let i = 1; i <= 4; i++) pages.push(i);
      pages.push('...', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '...');
      for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
    }

    return pages;
  };

  if (permissionsLoading) {
    return (
      <div className="p-6">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">Loading permissions...</p>
        </div>
      </div>
    );
  }

  if (!canAccessServicePlans) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-gray-900">Access Denied</h1>
          <p className="mt-2 text-sm text-gray-700">
            You do not have permission to access service plans.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 flex flex-col h-full min-h-0 overflow-hidden">
      <div className="bg-white shadow rounded-lg mb-2 flex-shrink-0">
        <div className="px-4 py-2">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Service Plans</h1>
              <p className="text-sm text-gray-600">Manage service plans based on offers.</p>
            </div>
            <div className="flex items-center gap-2">
              <form onSubmit={handleSearch} className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="Search by plan name or offer..."
                  className="px-3 py-1.5 w-64 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                />
                <button
                  type="submit"
                  className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  Search
                </button>
              </form>
              {canCreateServicePlans && (
                <Link
                  to="/service-plans/create"
                  className="inline-flex items-center px-3 py-1.5 text-sm border border-transparent rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-4 w-4" />
                  Create Plan
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {activeFilterChips.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-2 px-4 py-2 flex-shrink-0">
          <ActiveFilterChips
            chips={activeFilterChips}
            onRemove={handleRemoveFilterChip}
            onClearAll={handleClearAllFilters}
          />
        </div>
      )}

      <div className="bg-white shadow rounded-lg overflow-hidden flex-1 min-h-0 flex flex-col">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md m-4">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <div className="overflow-scroll flex-1" style={{ scrollbarGutter: 'stable' }}>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('planName', 'Plan Name')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('offer', 'Offer')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('type', 'Type')}
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {renderColumnHeader('status', 'Status')}
                    </th>
                    <th className="bg-gray-50 relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {plans.map((plan) => (
                    <tr key={plan.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{plan.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {plan.offer?.name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {plan.offer?.offerType || 'N/A'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadgeColor(plan.type)}`}>
                          {plan.type.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(plan.price)}</div>
                        <div className="text-sm text-gray-500">
                          Effective: {formatDate(plan.priceEffectiveDate)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(plan.startDate)} - {formatDate(plan.endDate)}
                        </div>
                        {plan.planProvisioningDate && (
                          <div className="text-sm text-gray-500">
                            Provisioning: {formatDate(plan.planProvisioningDate)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-1">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            !plan.inactive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {!plan.inactive ? 'Active' : 'Inactive'}
                          </span>
                          {plan.isTaxable && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                              Taxable
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex space-x-2">
                          <Link to={`/service-plans/${plan.id}`} className="text-blue-600 hover:text-blue-900" title="View Details">
                            <EyeIcon className="h-4 w-4" />
                          </Link>
                          {canUpdateServicePlans && (
                            <Link to={`/service-plans/${plan.id}/edit`} className="text-yellow-600 hover:text-yellow-900" title="Edit Plan">
                              <PencilIcon className="h-4 w-4" />
                            </Link>
                          )}
                          {canDeleteServicePlans && (
                            <button
                              onClick={() => handleDelete(plan.id, plan.name)}
                              className="text-red-600 hover:text-red-900"
                              title="Delete Plan"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {plans.length === 0 && !loading && (
              <div className="text-center py-12">
                <DocumentDuplicateIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No service plans</h3>
                <p className="mt-1 text-sm text-gray-500">
                  {canCreateServicePlans ? 'Get started by creating a new service plan.' : 'No service plans available.'}
                </p>
                {canCreateServicePlans && (
                  <div className="mt-6">
                    <Link
                      to="/service-plans/create"
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
                      Create Plan
                    </Link>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {!loading && total > 0 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 flex-shrink-0">
            <div className="flex-1 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label htmlFor="pageSize" className="text-sm text-gray-700">Show</label>
                  <select
                    id="pageSize"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setOffset(0);
                    }}
                    className="block rounded-md border-gray-300 py-1 pl-3 pr-8 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
                  >
                    {PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>{size}</option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-700">entries</span>
                </div>
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{offset + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(offset + pageSize, total)}</span> of{' '}
                    <span className="font-medium">{total}</span> entries
                  </p>
                </div>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button onClick={() => goToPage(1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">First</button>
                  <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Previous</button>
                  {getPageNumbers().map((page, index) => (
                    <React.Fragment key={index}>
                      {page === '...' ? (
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
                      ) : (
                        <button
                          onClick={() => goToPage(page as number)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                            currentPage === page ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                        >
                          {page}
                        </button>
                      )}
                    </React.Fragment>
                  ))}
                  <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Next</button>
                  <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">Last</button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanList;
